import React, { useState, useEffect } from 'react';
import {
  FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaTable,
  FaRulerCombined, FaSearch, FaChevronLeft, FaChevronRight, FaBoxes
} from 'react-icons/fa';

const API_BASE_URL = 'https://cloudapi.emedhatechnologies.com/pages/api/sheetsmaster';

const SheetsMaster = () => {
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    id: null,
    sheetName: '',
    category: 'Clothing',
    quantity: '',
    dimensions: '',
    unit: 'cm',
    owner: '',
    status: 'Active'
  });

  const [isEditing, setIsEditing] = useState(false);
  const [showForm, setShowForm] = useState(false);

  // Search and Pagination States
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Fetch data from MySQL database on mount
  useEffect(() => {
    fetchSheets();
  }, []);

  const fetchSheets = async () => {
    try {
      setLoading(true);
      const response = await fetch(API_BASE_URL);
      const data = await response.json();
      if (response.ok) {
        setSheets(data);
      } else {
        console.error(data.error);
      }
    } catch (error) {
      console.error("Failed to connect to backend server:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAddNew = () => {
    setFormData({ id: null, sheetName: '', category: 'Clothing', quantity: '', dimensions: '', unit: 'cm', owner: '', status: 'Active' });
    setIsEditing(false);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.sheetName || !formData.category || formData.quantity === '' || !formData.owner) {
      alert("Please fill in all required fields (including quantity).");
      return;
    }

    const payload = { ...formData, quantity: parseFloat(formData.quantity) };

    try {
      if (isEditing) {
        // UPDATE Request (PUT)
        const response = await fetch(`${API_BASE_URL}/${formData.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (response.ok) {
          fetchSheets();
        } else {
          alert('Failed to update record');
        }
      } else {
        // CREATE Request (POST)
        const response = await fetch(API_BASE_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (response.ok) {
          fetchSheets();
        } else {
          alert('Failed to create record');
        }
      }

      setShowForm(false);
      setFormData({ id: null, sheetName: '', category: 'Clothing', quantity: '', dimensions: '', unit: 'cm', owner: '', status: 'Active' });
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const handleEdit = (sheet) => {
    setFormData(sheet);
    setIsEditing(true);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this sheet record?")) {
      try {
        const response = await fetch(`${API_BASE_URL}/${id}`, {
          method: 'DELETE',
        });
        if (response.ok) {
          fetchSheets();
        } else {
          alert('Failed to delete record');
        }
      } catch (error) {
        console.error("Error deleting record:", error);
      }
    }
  };

  // Filter sheets based on search term
  const filteredSheets = sheets.filter(sheet =>
    sheet.sheetName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sheet.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sheet.owner.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination calculations
  const totalPages = Math.ceil(filteredSheets.length / itemsPerPage) || 1;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentSheets = filteredSheets.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <div style={{ padding: "12px", fontSize: "12px", fontFamily: "inherit", color: "#1e293b", background: "#f8fafc", minHeight: "100vh", boxSizing: "border-box", width: "100%", overflowX: "hidden" }}>

      {/* Top Header Section */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px", flexWrap: "wrap", gap: "8px" }}>
        <div>
          <h2 style={{ fontSize: "15px", fontWeight: "700", display: "flex", alignItems: "center", gap: "6px", margin: 0, color: "#0f172a" }}>
            <FaTable color="#2563eb" /> Products Master
          </h2>
          <p style={{ fontSize: "11px", color: "#64748b", margin: "2px 0 0 0" }}>Manage sheets, quantities, measurements, search, and configurations.</p>
        </div>
        {!showForm && (<button onClick={handleAddNew} style={{ display: "flex", alignItems: "center", gap: "5px", backgroundColor: "#2563eb", color: "#fff", border: "none", padding: "6px 12px", borderRadius: "5px", fontSize: "11px", fontWeight: "600", cursor: "pointer" }}><FaPlus /> Add Product</button>)}
      </div>

      {/* Search Bar & Controls Bar */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "12px", flexWrap: "wrap" }}>
        <div style={{ position: "relative", flex: "1", minWidth: "200px" }}>
          <FaSearch style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: "11px" }} />
          <input type="text" placeholder="Search by name, category, or owner..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} style={{ width: "100%", padding: "6px 10px 6px 30px", fontSize: "11px", border: "1px solid #cbd5e1", borderRadius: "5px", outline: "none", background: "#fff", boxSizing: "border-box" }} />
        </div>
      </div>

      {/* Form Card for Create & Edit */}
      {showForm && (
        <div style={{ background: "#ffffff", border: "1px solid #cbd5e1", borderRadius: "6px", padding: "12px", marginBottom: "16px", boxShadow: "0 2px 4px rgba(0, 0, 0, 0.04)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px", borderBottom: "1px solid #f1f5f9", paddingBottom: "6px" }}>
            <span style={{ fontWeight: "700", fontSize: "12px", color: "#334155" }}>
              {isEditing ? "Edit Product Record" : "New Product Record"}
            </span>
            <button
              onClick={() => setShowForm(false)}
              style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: "13px", color: "#64748b" }}
            >
              <FaTimes />
            </button>
          </div>

          <form onSubmit={handleSubmit} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "10px" }}>
            <div>
              <label style={{ display: "block", fontSize: "10px", fontWeight: "600", marginBottom: "3px", color: "#475569" }}>Product Name *</label>
              <input
                type="text"
                name="sheetName"
                value={formData.sheetName}
                onChange={handleChange}
                placeholder="e.g. Sales Tracker"
                style={{ width: "100%", padding: "5px 8px", fontSize: "11px", border: "1px solid #cbd5e1", borderRadius: "4px", outline: "none", boxSizing: "border-box" }}
              />
            </div>

            <div>
              <label style={{ display: "block", fontSize: "10px", fontWeight: "600", marginBottom: "3px", color: "#475569" }}>Category *</label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                style={{ width: "100%", padding: "5px 8px", fontSize: "11px", border: "1px solid #cbd5e1", borderRadius: "4px", outline: "none", background: "#fff", boxSizing: "border-box" }}
              >
                <option value="Clothing">Clothing</option>
                <option value="Fabric">Fabric</option>
                <option value="Apparel">Apparel</option>
                <option value="Material">Material</option>
                <option value="Logistics">Logistics</option>
                <option value="Accessories">Accessories</option>
              </select>
            </div>

            <div>
              <label style={{ display: "block", fontSize: "10px", fontWeight: "600", marginBottom: "3px", color: "#475569" }}>Quantity *</label>
              <input
                type="number"
                name="quantity"
                value={formData.quantity}
                onChange={handleChange}
                placeholder="e.g. 100"
                style={{ width: "100%", padding: "5px 8px", fontSize: "11px", border: "1px solid #cbd5e1", borderRadius: "4px", outline: "none", boxSizing: "border-box" }}
              />
            </div>

            <div>
              <label style={{ display: "block", fontSize: "10px", fontWeight: "600", marginBottom: "3px", color: "#475569" }}>Dimensions</label>
              <input
                type="text"
                name="dimensions"
                value={formData.dimensions}
                onChange={handleChange}
                placeholder="e.g. 100 x 200"
                style={{ width: "100%", padding: "5px 8px", fontSize: "11px", border: "1px solid #cbd5e1", borderRadius: "4px", outline: "none", boxSizing: "border-box" }}
              />
            </div>

            <div>
              <label style={{ display: "block", fontSize: "10px", fontWeight: "600", marginBottom: "3px", color: "#475569" }}>Unit Type</label>
              <select
                name="unit"
                value={formData.unit}
                onChange={handleChange}
                style={{ width: "100%", padding: "5px 8px", fontSize: "11px", border: "1px solid #cbd5e1", borderRadius: "4px", outline: "none", background: "#fff", boxSizing: "border-box" }}
              >
                <option value="cm">cm (Centimeters)</option>
                <option value="meters">m (Meters)</option>
                <option value="mm">mm (Millimeters)</option>
                <option value="inches">in (Inches)</option>
                <option value="yards">yd (Yards)</option>
              </select>
            </div>

            <div>
              <label style={{ display: "block", fontSize: "10px", fontWeight: "600", marginBottom: "3px", color: "#475569" }}>Owner *</label>
              <input
                type="text"
                name="owner"
                value={formData.owner}
                onChange={handleChange}
                placeholder="e.g. John Doe"
                style={{ width: "100%", padding: "5px 8px", fontSize: "11px", border: "1px solid #cbd5e1", borderRadius: "4px", outline: "none", boxSizing: "border-box" }}
              />
            </div>

            <div>
              <label style={{ display: "block", fontSize: "10px", fontWeight: "600", marginBottom: "3px", color: "#475569" }}>Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                style={{ width: "100%", padding: "5px 8px", fontSize: "11px", border: "1px solid #cbd5e1", borderRadius: "4px", outline: "none", background: "#fff", boxSizing: "border-box" }}
              >
                <option value="Active">Active</option>
                <option value="Pending">Pending</option>
                <option value="Archived">Archived</option>
              </select>
            </div>

            <div style={{ gridColumn: "1 / -1", display: "flex", justifyContent: "flex-end", gap: "6px", marginTop: "4px" }}>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                style={{ backgroundColor: "#e2e8f0", color: "#334155", border: "none", padding: "5px 10px", borderRadius: "4px", fontSize: "11px", fontWeight: "600", cursor: "pointer" }}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={{ display: "flex", alignItems: "center", gap: "4px", backgroundColor: "#16a34a", color: "#fff", border: "none", padding: "5px 10px", borderRadius: "4px", fontSize: "11px", fontWeight: "600", cursor: "pointer" }}
              >
                <FaSave /> {isEditing ? "Update" : "Save"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Responsive Cards Layout */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "10px" }}>
        {loading ? (
          <div style={{ gridColumn: "1 / -1", textAlign: "center", padding: "20px", color: "#64748b" }}>Loading records from database...</div>
        ) : currentSheets.length > 0 ? (
          currentSheets.map((sheet) => (
            <div
              key={sheet.id}
              style={{
                background: "#ffffff",
                border: "1px solid #e2e8f0",
                borderRadius: "6px",
                padding: "10px 12px",
                boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
                display: "flex",
                flexDirection: "column",
                gap: "6px"
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <span style={{ fontSize: "10px", fontWeight: "700", textTransform: "uppercase", color: "#2563eb", background: "#eff6ff", padding: "1px 5px", borderRadius: "3px" }}>
                    {sheet.category}
                  </span>
                  <h3 style={{ fontSize: "13px", fontWeight: "700", margin: "4px 0 2px 0", color: "#0f172a" }}>
                    {sheet.sheetName}
                  </h3>
                </div>
                <span style={{
                  padding: "1px 6px",
                  fontSize: "9px",
                  fontWeight: "600",
                  borderRadius: "10px",
                  backgroundColor: sheet.status === 'Active' ? "#dcfce7" : sheet.status === 'Pending' ? "#fef9c3" : "#f1f5f9",
                  color: sheet.status === 'Active' ? "#15803d" : sheet.status === 'Pending' ? "#a16207" : "#475569"
                }}>
                  {sheet.status}
                </span>
              </div>

              {/* Grid details including quantity */}
              <div style={{ fontSize: "11px", color: "#475569", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "4px", background: "#f8fafc", padding: "6px", borderRadius: "4px", textAlign: "center" }}>
                <div>
                  <span style={{ color: "#94a3b8", display: "block", fontSize: "9px" }}>Quantity</span>
                  <span style={{ fontWeight: "700", display: "flex", alignItems: "center", justifyContent: "center", gap: "3px", color: "#0f172a" }}>
                    <FaBoxes size={9} color="#64748b" /> {sheet.quantity}
                  </span>
                </div>
                <div>
                  <span style={{ color: "#94a3b8", display: "block", fontSize: "9px" }}>Dimensions</span>
                  <span style={{ fontWeight: "600", display: "flex", alignItems: "center", justifyContent: "center", gap: "3px" }}>
                    <FaRulerCombined size={9} color="#64748b" /> {sheet.dimensions ? `${sheet.dimensions} ${sheet.unit}` : 'N/A'}
                  </span>
                </div>
                <div>
                  <span style={{ color: "#94a3b8", display: "block", fontSize: "9px" }}>Owner</span>
                  <span style={{ fontWeight: "600" }}>{sheet.owner}</span>
                </div>
              </div>

              {/* Action Icons Row */}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "6px", marginTop: "2px", borderTop: "1px solid #f1f5f9", paddingTop: "6px" }}>
                <button
                  onClick={() => handleEdit(sheet)}
                  style={{ background: "#eff6ff", color: "#2563eb", border: "none", padding: "4px 8px", borderRadius: "4px", fontSize: "11px", cursor: "pointer", display: "flex", alignItems: "center", gap: "3px", fontWeight: "600" }}
                  title="Edit"
                >
                  <FaEdit size={11} /> Edit
                </button>
                <button
                  onClick={() => handleDelete(sheet.id)}
                  style={{ background: "#fef2f2", color: "#dc2626", border: "none", padding: "4px 8px", borderRadius: "4px", fontSize: "11px", cursor: "pointer", display: "flex", alignItems: "center", gap: "3px", fontWeight: "600" }}
                  title="Delete"
                >
                  <FaTrash size={11} /> Delete
                </button>
              </div>
            </div>
          ))
        ) : (
          <div style={{ gridColumn: "1 / -1", textAlign: "center", padding: "20px", color: "#94a3b8", background: "#fff", borderRadius: "6px", border: "1px solid #e2e8f0" }}>
            No matching sheet records found.
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {filteredSheets.length > itemsPerPage && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "16px", background: "#fff", padding: "8px 12px", borderRadius: "6px", border: "1px solid #e2e8f0" }}>
          <span style={{ fontSize: "11px", color: "#64748b" }}>
            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredSheets.length)} of {filteredSheets.length} entries
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              style={{ display: "flex", alignItems: "center", gap: "3px", background: currentPage === 1 ? "#f1f5f9" : "#eff6ff", color: currentPage === 1 ? "#94a3b8" : "#2563eb", border: "none", padding: "4px 8px", borderRadius: "4px", fontSize: "11px", cursor: currentPage === 1 ? "not-allowed" : "pointer", fontWeight: "600" }}
            >
              <FaChevronLeft size={10} /> Prev
            </button>
            <span style={{ fontSize: "11px", fontWeight: "600", padding: "0 4px" }}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              style={{ display: "flex", alignItems: "center", gap: "3px", background: currentPage === totalPages ? "#f1f5f9" : "#eff6ff", color: currentPage === totalPages ? "#94a3b8" : "#2563eb", border: "none", padding: "4px 8px", borderRadius: "4px", fontSize: "11px", cursor: currentPage === totalPages ? "not-allowed" : "pointer", fontWeight: "600" }}
            >
              Next <FaChevronRight size={10} />
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default SheetsMaster;







// import React, { useState, useEffect } from 'react';
// import {
//   FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaTable,
//   FaRulerCombined, FaSearch, FaChevronLeft, FaChevronRight, FaBoxes
// } from 'react-icons/fa';

// const API_BASE_URL = 'https://cloudapi.emedhatechnologies.com/pages/api/sheetsmaster';

// const SheetsMaster = () => {
//   const [sheets, setSheets] = useState([]);
//   const [loading, setLoading] = useState(true);

//   const [formData, setFormData] = useState({
//     id: null,
//     sheetName: '',
//     category: 'Clothing',
//     quantity: 1, // Default to 1
//     dimensions: '',
//     unit: 'cm',
//     owner: '',
//     status: 'Active'
//   });

//   const [isEditing, setIsEditing] = useState(false);
//   const [showForm, setShowForm] = useState(false);

//   // Search and Pagination States
//   const [searchTerm, setSearchTerm] = useState('');
//   const [currentPage, setCurrentPage] = useState(1);
//   const itemsPerPage = 20;

//   // Fetch data from MySQL database on mount
//   useEffect(() => {
//     fetchSheets();
//   }, []);

//   const fetchSheets = async () => {
//     try {
//       setLoading(true);
//       const response = await fetch(API_BASE_URL);
//       const data = await response.json();
//       if (response.ok) {
//         setSheets(data);
//       } else {
//         console.error(data.error);
//       }
//     } catch (error) {
//       console.error("Failed to connect to backend server:", error);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleChange = (e) => {
//     setFormData({ ...formData, [e.target.name]: e.target.value });
//   };

//   const handleAddNew = () => {
//     setFormData({ id: null, sheetName: '', category: 'Clothing', quantity: 1, dimensions: '', unit: 'cm', owner: '', status: 'Active' });
//     setIsEditing(false);
//     setShowForm(true);
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!formData.sheetName || !formData.category || !formData.owner) {
//       alert("Please fill in all required fields.");
//       return;
//     }

//     const payload = { ...formData, quantity: parseFloat(formData.quantity) || 1 };

//     try {
//       if (isEditing) {
//         // UPDATE Request (PUT)
//         const response = await fetch(`${API_BASE_URL}/${formData.id}`, {
//           method: 'PUT',
//           headers: { 'Content-Type': 'application/json' },
//           body: JSON.stringify(payload)
//         });
//         if (response.ok) {
//           fetchSheets();
//         } else {
//           alert('Failed to update record');
//         }
//       } else {
//         // CREATE Request (POST)
//         const response = await fetch(API_BASE_URL, {
//           method: 'POST',
//           headers: { 'Content-Type': 'application/json' },
//           body: JSON.stringify(payload)
//         });
//         if (response.ok) {
//           fetchSheets();
//         } else {
//           alert('Failed to create record');
//         }
//       }

//       setShowForm(false);
//       setFormData({ id: null, sheetName: '', category: 'Clothing', quantity: 1, dimensions: '', unit: 'cm', owner: '', status: 'Active' });
//     } catch (error) {
//       console.error("Error submitting form:", error);
//     }
//   };

//   const handleEdit = (sheet) => {
//     setFormData({
//       ...sheet,
//       quantity: sheet.quantity !== undefined && sheet.quantity !== null ? sheet.quantity : 1
//     });
//     setIsEditing(true);
//     setShowForm(true);
//   };

//   const handleDelete = async (id) => {
//     if (window.confirm("Are you sure you want to delete this sheet record?")) {
//       try {
//         const response = await fetch(`${API_BASE_URL}/${id}`, {
//           method: 'DELETE',
//         });
//         if (response.ok) {
//           fetchSheets();
//         } else {
//           alert('Failed to delete record');
//         }
//       } catch (error) {
//         console.error("Error deleting record:", error);
//       }
//     }
//   };

//   // Filter sheets based on search term
//   const filteredSheets = sheets.filter(sheet =>
//     sheet.sheetName.toLowerCase().includes(searchTerm.toLowerCase()) ||
//     sheet.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
//     sheet.owner.toLowerCase().includes(searchTerm.toLowerCase())
//   );

//   // Pagination calculations
//   const totalPages = Math.ceil(filteredSheets.length / itemsPerPage) || 1;
//   const indexOfLastItem = currentPage * itemsPerPage;
//   const indexOfFirstItem = indexOfLastItem - itemsPerPage;
//   const currentSheets = filteredSheets.slice(indexOfFirstItem, indexOfLastItem);

//   return (
//     <div style={{ padding: "12px", fontSize: "12px", fontFamily: "inherit", color: "#1e293b", background: "#f8fafc", minHeight: "100vh", boxSizing: "border-box", width: "100%", overflowX: "hidden" }}>

//       {/* Top Header Section */}
//       <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px", flexWrap: "wrap", gap: "8px" }}>
//         <div>
//           <h2 style={{ fontSize: "15px", fontWeight: "700", display: "flex", alignItems: "center", gap: "6px", margin: 0, color: "#0f172a" }}>
//             <FaTable color="#2563eb" /> Products Master
//           </h2>
//           <p style={{ fontSize: "11px", color: "#64748b", margin: "2px 0 0 0" }}>Manage sheets, quantities, measurements, search, and configurations.</p>
//         </div>
//         {!showForm && (<button onClick={handleAddNew} style={{ display: "flex", alignItems: "center", gap: "5px", backgroundColor: "#2563eb", color: "#fff", border: "none", padding: "6px 12px", borderRadius: "5px", fontSize: "11px", fontWeight: "600", cursor: "pointer" }}><FaPlus /> Add Product</button>)}
//       </div>

//       {/* Search Bar & Controls Bar */}
//       <div style={{ display: "flex", gap: "8px", marginBottom: "12px", flexWrap: "wrap" }}>
//         <div style={{ position: "relative", flex: "1", minWidth: "200px" }}>
//           <FaSearch style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: "11px" }} />
//           <input type="text" placeholder="Search by name, category, or owner..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} style={{ width: "100%", padding: "6px 10px 6px 30px", fontSize: "11px", border: "1px solid #cbd5e1", borderRadius: "5px", outline: "none", background: "#fff", boxSizing: "border-box" }} />
//         </div>
//       </div>

//       {/* Form Card for Create & Edit */}
//       {showForm && (
//         <div style={{ background: "#ffffff", border: "1px solid #cbd5e1", borderRadius: "6px", padding: "12px", marginBottom: "16px", boxShadow: "0 2px 4px rgba(0, 0, 0, 0.04)" }}>
//           <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px", borderBottom: "1px solid #f1f5f9", paddingBottom: "6px" }}>
//             <span style={{ fontWeight: "700", fontSize: "12px", color: "#334155" }}>
//               {isEditing ? "Edit Product Record" : "New Product Record"}
//             </span>
//             <button
//               onClick={() => setShowForm(false)}
//               style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: "13px", color: "#64748b" }}
//             >
//               <FaTimes />
//             </button>
//           </div>

//           <form onSubmit={handleSubmit} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "10px" }}>
//             <div>
//               <label style={{ display: "block", fontSize: "10px", fontWeight: "600", marginBottom: "3px", color: "#475569" }}>Product Name *</label>
//               <input
//                 type="text"
//                 name="sheetName"
//                 value={formData.sheetName}
//                 onChange={handleChange}
//                 placeholder="e.g. Sales Tracker"
//                 style={{ width: "100%", padding: "5px 8px", fontSize: "11px", border: "1px solid #cbd5e1", borderRadius: "4px", outline: "none", boxSizing: "border-box" }}
//               />
//             </div>

//             <div>
//               <label style={{ display: "block", fontSize: "10px", fontWeight: "600", marginBottom: "3px", color: "#475569" }}>Category *</label>
//               <select
//                 name="category"
//                 value={formData.category}
//                 onChange={handleChange}
//                 style={{ width: "100%", padding: "5px 8px", fontSize: "11px", border: "1px solid #cbd5e1", borderRadius: "4px", outline: "none", background: "#fff", boxSizing: "border-box" }}
//               >
//                 <option value="Clothing">Clothing</option>
//                 <option value="Fabric">Fabric</option>
//                 <option value="Apparel">Apparel</option>
//                 <option value="Material">Material</option>
//                 <option value="Logistics">Logistics</option>
//                 <option value="Accessories">Accessories</option>
//               </select>
//             </div>

//             <div>
//               <label style={{ display: "block", fontSize: "10px", fontWeight: "600", marginBottom: "3px", color: "#475569" }}>Quantity (Default 1)</label>
//               <input
//                 type="number"
//                 name="quantity"
//                 value={formData.quantity}
//                 disabled={true}
//                 style={{ width: "100%", padding: "5px 8px", fontSize: "11px", border: "1px solid #cbd5e1", borderRadius: "4px", outline: "none", background: "#f1f5f9", color: "#64748b", cursor: "not-allowed", boxSizing: "border-box" }}
//               />
//             </div>

//             <div>
//               <label style={{ display: "block", fontSize: "10px", fontWeight: "600", marginBottom: "3px", color: "#475569" }}>Dimensions</label>
//               <input
//                 type="text"
//                 name="dimensions"
//                 value={formData.dimensions}
//                 onChange={handleChange}
//                 placeholder="e.g. 100 x 200"
//                 style={{ width: "100%", padding: "5px 8px", fontSize: "11px", border: "1px solid #cbd5e1", borderRadius: "4px", outline: "none", boxSizing: "border-box" }}
//               />
//             </div>

//             <div>
//               <label style={{ display: "block", fontSize: "10px", fontWeight: "600", marginBottom: "3px", color: "#475569" }}>Unit Type</label>
//               <select
//                 name="unit"
//                 value={formData.unit}
//                 onChange={handleChange}
//                 style={{ width: "100%", padding: "5px 8px", fontSize: "11px", border: "1px solid #cbd5e1", borderRadius: "4px", outline: "none", background: "#fff", boxSizing: "border-box" }}
//               >
//                 <option value="cm">cm (Centimeters)</option>
//                 <option value="meters">m (Meters)</option>
//                 <option value="mm">mm (Millimeters)</option>
//                 <option value="inches">in (Inches)</option>
//                 <option value="yards">yd (Yards)</option>
//               </select>
//             </div>

//             <div>
//               <label style={{ display: "block", fontSize: "10px", fontWeight: "600", marginBottom: "3px", color: "#475569" }}>Owner *</label>
//               <input
//                 type="text"
//                 name="owner"
//                 value={formData.owner}
//                 onChange={handleChange}
//                 placeholder="e.g. John Doe"
//                 style={{ width: "100%", padding: "5px 8px", fontSize: "11px", border: "1px solid #cbd5e1", borderRadius: "4px", outline: "none", boxSizing: "border-box" }}
//               />
//             </div>

//             <div>
//               <label style={{ display: "block", fontSize: "10px", fontWeight: "600", marginBottom: "3px", color: "#475569" }}>Status</label>
//               <select
//                 name="status"
//                 value={formData.status}
//                 onChange={handleChange}
//                 style={{ width: "100%", padding: "5px 8px", fontSize: "11px", border: "1px solid #cbd5e1", borderRadius: "4px", outline: "none", background: "#fff", boxSizing: "border-box" }}
//               >
//                 <option value="Active">Active</option>
//                 <option value="Pending">Pending</option>
//                 <option value="Archived">Archived</option>
//               </select>
//             </div>

//             <div style={{ gridColumn: "1 / -1", display: "flex", justifyContent: "flex-end", gap: "6px", marginTop: "4px" }}>
//               <button
//                 type="button"
//                 onClick={() => setShowForm(false)}
//                 style={{ backgroundColor: "#e2e8f0", color: "#334155", border: "none", padding: "5px 10px", borderRadius: "4px", fontSize: "11px", fontWeight: "600", cursor: "pointer" }}
//               >
//                 Cancel
//               </button>
//               <button
//                 type="submit"
//                 style={{ display: "flex", alignItems: "center", gap: "4px", backgroundColor: "#16a34a", color: "#fff", border: "none", padding: "5px 10px", borderRadius: "4px", fontSize: "11px", fontWeight: "600", cursor: "pointer" }}
//               >
//                 <FaSave /> {isEditing ? "Update" : "Save"}
//               </button>
//             </div>
//           </form>
//         </div>
//       )}

//       {/* Responsive Cards Layout */}
//       <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "10px" }}>
//         {loading ? (
//           <div style={{ gridColumn: "1 / -1", textAlign: "center", padding: "20px", color: "#64748b" }}>Loading records from database...</div>
//         ) : currentSheets.length > 0 ? (
//           currentSheets.map((sheet) => (
//             <div
//               key={sheet.id}
//               style={{
//                 background: "#ffffff",
//                 border: "1px solid #e2e8f0",
//                 borderRadius: "6px",
//                 padding: "10px 12px",
//                 boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
//                 display: "flex",
//                 flexDirection: "column",
//                 gap: "6px"
//               }}
//             >
//               <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
//                 <div>
//                   <span style={{ fontSize: "10px", fontWeight: "700", textTransform: "uppercase", color: "#2563eb", background: "#eff6ff", padding: "1px 5px", borderRadius: "3px" }}>
//                     {sheet.category}
//                   </span>
//                   <h3 style={{ fontSize: "13px", fontWeight: "700", margin: "4px 0 2px 0", color: "#0f172a" }}>
//                     {sheet.sheetName}
//                   </h3>
//                 </div>
//                 <span style={{
//                   padding: "1px 6px",
//                   fontSize: "9px",
//                   fontWeight: "600",
//                   borderRadius: "10px",
//                   backgroundColor: sheet.status === 'Active' ? "#dcfce7" : sheet.status === 'Pending' ? "#fef9c3" : "#f1f5f9",
//                   color: sheet.status === 'Active' ? "#15803d" : sheet.status === 'Pending' ? "#a16207" : "#475569"
//                 }}>
//                   {sheet.status}
//                 </span>
//               </div>

//               {/* Grid details including quantity */}
//               <div style={{ fontSize: "11px", color: "#475569", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "4px", background: "#f8fafc", padding: "6px", borderRadius: "4px", textAlign: "center" }}>
//                 <div>
//                   <span style={{ color: "#94a3b8", display: "block", fontSize: "9px" }}>Quantity</span>
//                   <span style={{ fontWeight: "700", display: "flex", alignItems: "center", justifyContent: "center", gap: "3px", color: "#0f172a" }}>
//                     <FaBoxes size={9} color="#64748b" /> {sheet.quantity ?? 1}
//                   </span>
//                 </div>
//                 <div>
//                   <span style={{ color: "#94a3b8", display: "block", fontSize: "9px" }}>Dimensions</span>
//                   <span style={{ fontWeight: "600", display: "flex", alignItems: "center", justifyContent: "center", gap: "3px" }}>
//                     <FaRulerCombined size={9} color="#64748b" /> {sheet.dimensions ? `${sheet.dimensions} ${sheet.unit}` : 'N/A'}
//                   </span>
//                 </div>
//                 <div>
//                   <span style={{ color: "#94a3b8", display: "block", fontSize: "9px" }}>Owner</span>
//                   <span style={{ fontWeight: "600" }}>{sheet.owner}</span>
//                 </div>
//               </div>

//               {/* Action Icons Row */}
//               <div style={{ display: "flex", justifyContent: "flex-end", gap: "6px", marginTop: "2px", borderTop: "1px solid #f1f5f9", paddingTop: "6px" }}>
//                 <button
//                   onClick={() => handleEdit(sheet)}
//                   style={{ background: "#eff6ff", color: "#2563eb", border: "none", padding: "4px 8px", borderRadius: "4px", fontSize: "11px", cursor: "pointer", display: "flex", alignItems: "center", gap: "3px", fontWeight: "600" }}
//                   title="Edit"
//                 >
//                   <FaEdit size={11} /> Edit
//                 </button>
//                 <button
//                   onClick={() => handleDelete(sheet.id)}
//                   style={{ background: "#fef2f2", color: "#dc2626", border: "none", padding: "4px 8px", borderRadius: "4px", fontSize: "11px", cursor: "pointer", display: "flex", alignItems: "center", gap: "3px", fontWeight: "600" }}
//                   title="Delete"
//                 >
//                   <FaTrash size={11} /> Delete
//                 </button>
//               </div>
//             </div>
//           ))
//         ) : (
//           <div style={{ gridColumn: "1 / -1", textAlign: "center", padding: "20px", color: "#94a3b8", background: "#fff", borderRadius: "6px", border: "1px solid #e2e8f0" }}>
//             No matching sheet records found.
//           </div>
//         )}
//       </div>

//       {/* Pagination Controls */}
//       {filteredSheets.length > itemsPerPage && (
//         <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "16px", background: "#fff", padding: "8px 12px", borderRadius: "6px", border: "1px solid #e2e8f0" }}>
//           <span style={{ fontSize: "11px", color: "#64748b" }}>
//             Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredSheets.length)} of {filteredSheets.length} entries
//           </span>
//           <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
//             <button
//               onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
//               disabled={currentPage === 1}
//               style={{ display: "flex", alignItems: "center", gap: "3px", background: currentPage === 1 ? "#f1f5f9" : "#eff6ff", color: currentPage === 1 ? "#94a3b8" : "#2563eb", border: "none", padding: "4px 8px", borderRadius: "4px", fontSize: "11px", cursor: currentPage === 1 ? "not-allowed" : "pointer", fontWeight: "600" }}
//             >
//               <FaChevronLeft size={10} /> Prev
//             </button>
//             <span style={{ fontSize: "11px", fontWeight: "600", padding: "0 4px" }}>
//               Page {currentPage} of {totalPages}
//             </span>
//             <button
//               onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
//               disabled={currentPage === totalPages}
//               style={{ display: "flex", alignItems: "center", gap: "3px", background: currentPage === totalPages ? "#f1f5f9" : "#eff6ff", color: currentPage === totalPages ? "#94a3b8" : "#2563eb", border: "none", padding: "4px 8px", borderRadius: "4px", fontSize: "11px", cursor: currentPage === totalPages ? "not-allowed" : "pointer", fontWeight: "600" }}
//             >
//               Next <FaChevronRight size={10} />
//             </button>
//           </div>
//         </div>
//       )}

//     </div>
//   );
// };

// export default SheetsMaster;